// Advanced crossword grid generator
export function generateCrosswordGrid(clues) {
  const words = clues.map((clue, index) => ({
    id: index,
    clue: clue.clue,
    answer: clue.answer.toUpperCase(),
    placed: false
  }));

  // Sort words by length (longer first for better placement)
  words.sort((a, b) => b.answer.length - a.answer.length);

  // Determine grid size based on words
  const maxLength = Math.max(...words.map(w => w.answer.length));
  const gridSize = Math.max(10, Math.min(15, maxLength + 5));

  // Initialize empty grid
  const grid = Array(gridSize).fill(null).map(() => 
    Array(gridSize).fill(null).map(() => ({ 
      letter: '', 
      isBlack: false, 
      number: null 
    }))
  );

  const placedWords = [];
  
  // Place first word horizontally in the center
  const firstWord = words[0];
  const startRow = Math.floor(gridSize / 2);
  const startCol = Math.floor((gridSize - firstWord.answer.length) / 2);
  
  const firstWordCells = [];
  for (let i = 0; i < firstWord.answer.length; i++) {
    grid[startRow][startCol + i].letter = firstWord.answer[i];
    firstWordCells.push({ row: startRow, col: startCol + i });
  }
  
  placedWords.push({
    ...firstWord,
    direction: 'horizontal',
    startRow,
    startCol,
    cells: firstWordCells,
    placed: true
  });

  // Try to place remaining words
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    let placed = false;

    // Try to find intersections with already placed words
    for (const placedWord of placedWords) {
      if (placed) break;

      for (let j = 0; j < word.answer.length; j++) {
        if (placed) break;
        
        for (let k = 0; k < placedWord.answer.length; k++) {
          if (word.answer[j] === placedWord.answer[k]) {
            // Found potential intersection
            const direction = placedWord.direction === 'horizontal' ? 'vertical' : 'horizontal';
            
            let newStartRow, newStartCol;
            
            if (direction === 'vertical') {
              newStartRow = placedWord.startRow - j;
              newStartCol = placedWord.startCol + k;
            } else {
              newStartRow = placedWord.startRow + k;
              newStartCol = placedWord.startCol - j;
            }

            // Check if word fits and doesn't conflict
            if (canPlaceWord(grid, word.answer, newStartRow, newStartCol, direction, gridSize)) {
              const wordCells = placeWord(grid, word.answer, newStartRow, newStartCol, direction);
              
              placedWords.push({
                ...word,
                direction,
                startRow: newStartRow,
                startCol: newStartCol,
                cells: wordCells,
                placed: true
              });
              
              placed = true;
              break;
            }
          }
        }
      }
    }

    // If couldn't place with intersection, try to place separately
    if (!placed) {
      const direction = Math.random() > 0.5 ? 'horizontal' : 'vertical';
      
      for (let row = 0; row < gridSize && !placed; row++) {
        for (let col = 0; col < gridSize && !placed; col++) {
          if (canPlaceWord(grid, word.answer, row, col, direction, gridSize)) {
            const wordCells = placeWord(grid, word.answer, row, col, direction);
            
            placedWords.push({
              ...word,
              direction,
              startRow: row,
              startCol: col,
              cells: wordCells,
              placed: true
            });
            
            placed = true;
          }
        }
      }
    }
  }

  // Fill unused cells as black
  fillBlackCells(grid, placedWords, gridSize);
  
  // Number the words
  numberWords(grid, placedWords);

  return {
    grid,
    words: placedWords,
    size: gridSize
  };
}

function canPlaceWord(grid, word, startRow, startCol, direction, gridSize) {
  if (direction === 'horizontal') {
    if (startCol + word.length > gridSize || startRow < 0 || startRow >= gridSize) {
      return false;
    }
    
    // Check for conflicts
    for (let i = 0; i < word.length; i++) {
      const cell = grid[startRow][startCol + i];
      if (cell.letter && cell.letter !== word[i]) {
        return false;
      }
    }
    
    // Check boundaries (no adjacent words)
    if (startCol > 0 && grid[startRow][startCol - 1].letter) return false;
    if (startCol + word.length < gridSize && grid[startRow][startCol + word.length].letter) return false;
    
  } else { // vertical
    if (startRow + word.length > gridSize || startCol < 0 || startCol >= gridSize) {
      return false;
    }
    
    // Check for conflicts
    for (let i = 0; i < word.length; i++) {
      const cell = grid[startRow + i][startCol];
      if (cell.letter && cell.letter !== word[i]) {
        return false;
      }
    }
    
    // Check boundaries
    if (startRow > 0 && grid[startRow - 1][startCol].letter) return false;
    if (startRow + word.length < gridSize && grid[startRow + word.length][startCol].letter) return false;
  }
  
  return true;
}

function placeWord(grid, word, startRow, startCol, direction) {
  const cells = [];
  
  for (let i = 0; i < word.length; i++) {
    const row = direction === 'horizontal' ? startRow : startRow + i;
    const col = direction === 'horizontal' ? startCol + i : startCol;
    
    grid[row][col].letter = word[i];
    cells.push({ row, col });
  }
  
  return cells;
}

function fillBlackCells(grid, placedWords, gridSize) {
  // Create a map of used cells
  const usedCells = new Set();
  placedWords.forEach(word => {
    word.cells.forEach(cell => {
      usedCells.add(`${cell.row}-${cell.col}`);
    });
  });

  // Mark unused cells as black
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      if (!usedCells.has(`${row}-${col}`)) {
        grid[row][col].isBlack = true;
      }
    }
  }
}

function numberWords(grid, placedWords) {
  // Sort words by position for consistent numbering
  const sortedWords = [...placedWords].sort((a, b) => {
    if (a.startRow !== b.startRow) return a.startRow - b.startRow;
    return a.startCol - b.startCol;
  });

  let number = 1;
  const numberedPositions = new Set();

  sortedWords.forEach(word => {
    const key = `${word.startRow}-${word.startCol}`;
    
    if (!numberedPositions.has(key)) {
      grid[word.startRow][word.startCol].number = number;
      word.number = number;
      numberedPositions.add(key);
      number++;
    } else {
      // Find existing number for this position
      word.number = grid[word.startRow][word.startCol].number;
    }
  });
}